/**
 * Section: Hero Dual Video Sequence (custom-hero.js)
 * Supports Desktop & Mobile Video Inputs with Smart Fallback
 */

(function () {
  'use strict';

  function initHeroVideoSequence() {
    const heroSection = document.querySelector('.juli-hero-section');
    if (!heroSection) return;

    let skipPreloader = false;
    try {
      if (sessionStorage.getItem('juli_skip_hero_preloader') === 'true') {
        skipPreloader = true;
        sessionStorage.removeItem('juli_skip_hero_preloader');
      }
    } catch (err) {}

    const isPreloaderEnabled = heroSection.dataset.enablePreloader === 'true' && !skipPreloader;
    const isMobile = window.innerWidth <= 768;

    const videoIntro = isMobile
      ? (heroSection.querySelector('.juli-hero-video--intro.juli-hero-video--mobile') || heroSection.querySelector('.juli-hero-video--intro'))
      : (heroSection.querySelector('.juli-hero-video--intro.juli-hero-video--desktop') || heroSection.querySelector('.juli-hero-video--intro'));

    const videoLoop = isMobile
      ? (heroSection.querySelector('.juli-hero-video--loop.juli-hero-video--mobile') || heroSection.querySelector('.juli-hero-video--loop'))
      : (heroSection.querySelector('.juli-hero-video--loop.juli-hero-video--desktop') || heroSection.querySelector('.juli-hero-video--loop'));

    const singleVideo = heroSection.querySelector('.juli-hero-bg-video:not(.juli-hero-video--intro):not(.juli-hero-video--loop)');
    const transitionTime = parseFloat(heroSection.dataset.transitionTime || '7.15');

    // Prepare video elements specifically for iOS Safari & Low Power Mode
    function prepareVideoForAutoplay(videoEl) {
      if (!videoEl) return;
      videoEl.muted = true;
      videoEl.defaultMuted = true;
      videoEl.playsInline = true;
      videoEl.setAttribute('playsinline', '');
      videoEl.setAttribute('webkit-playsinline', '');
      videoEl.setAttribute('muted', '');
      videoEl.setAttribute('autoplay', '');
      videoEl.setAttribute('disablepictureinpicture', '');
      videoEl.setAttribute('disableremoteplayback', '');
    }

    prepareVideoForAutoplay(videoIntro);
    prepareVideoForAutoplay(videoLoop);
    prepareVideoForAutoplay(singleVideo);

    const primaryVideo = videoIntro || singleVideo;

    if (!primaryVideo) {
      heroSection.classList.add('is-revealed');
      document.body.classList.add('juli-site-loaded');
      return;
    }

    // Helper to safely play video across all iOS & Android battery saver modes
    function safePlayVideo(videoEl) {
      if (!videoEl) return Promise.reject();
      prepareVideoForAutoplay(videoEl);
      const playPromise = videoEl.play();
      if (playPromise !== undefined) {
        return playPromise.catch((err) => {
          console.warn('[Hero Video] Autoplay restricted (e.g. iOS Low Power Mode):', err);
          // If autoplay fails, quickly reveal UI so page is not stuck
          setTimeout(revealUI, 300);
        });
      }
      return Promise.resolve();
    }

    let hasRevealedUI = false;
    let hasSwitchedToLoop = false;

    // On iOS Low Power Mode, autoplay is blocked until first user touch gesture
    function kickstartPlaybackOnUserTouch() {
      const activeVideo = (hasSwitchedToLoop ? videoLoop : primaryVideo);
      if (activeVideo && activeVideo.paused) {
        safePlayVideo(activeVideo);
      }
    }

    window.addEventListener('touchstart', kickstartPlaybackOnUserTouch, { once: true, passive: true });
    window.addEventListener('pointerdown', kickstartPlaybackOnUserTouch, { once: true, passive: true });
    window.addEventListener('scroll', kickstartPlaybackOnUserTouch, { once: true, passive: true });

    if (!isPreloaderEnabled) {
      heroSection.classList.add('is-revealed');
      document.body.classList.add('juli-site-loaded');
      window.dispatchEvent(new CustomEvent('juli:intro-completed'));
      if (videoIntro && videoLoop) {
        videoLoop.style.opacity = '1';
        safePlayVideo(videoLoop);
        videoIntro.style.display = 'none';
      }
      return;
    }

    // Lock page scroll to top (0,0) during preloader phase
    window.scrollTo(0, 0);
    heroSection.classList.add('is-preloading');
    document.documentElement.classList.add('juli-preloading-active');
    document.body.classList.add('juli-preloading-active');

    function preventScroll(e) {
      if (document.body.classList.contains('juli-preloading-active')) {
        e.preventDefault();
      }
    }

    window.addEventListener('wheel', preventScroll, { passive: false });
    window.addEventListener('touchmove', preventScroll, { passive: false });
    window.addEventListener('keydown', (e) => {
      if (document.body.classList.contains('juli-preloading-active')) {
        if (['ArrowDown', 'ArrowUp', 'Space', 'PageDown', 'PageUp', 'Home', 'End'].includes(e.code)) {
          e.preventDefault();
        }
      }
    });

    function revealUI() {
      if (hasRevealedUI) return;
      hasRevealedUI = true;

      // Unlock scrolling
      document.documentElement.classList.remove('juli-preloading-active');
      document.body.classList.remove('juli-preloading-active');
      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('touchmove', preventScroll);

      heroSection.classList.remove('is-preloading');
      heroSection.classList.add('is-revealed');
      document.body.classList.add('juli-site-loaded');
      window.dispatchEvent(new CustomEvent('juli:intro-completed'));
    }

    function switchToLoopVideo() {
      if (hasSwitchedToLoop || !videoLoop) return;
      hasSwitchedToLoop = true;

      revealUI();

      // Start looping video 2 strictly when video 1 finishes
      safePlayVideo(videoLoop).then(() => {
        videoLoop.style.opacity = '1';
        setTimeout(() => {
          if (videoIntro) {
            videoIntro.pause();
            videoIntro.style.display = 'none';
          }
        }, 400);
      }).catch(() => {
        videoLoop.style.opacity = '1';
      });
    }

    if (videoIntro) {
      // 1. Timeupdate: Trigger UI reveal at 7.15s (unlocks scroll & displays UI)
      videoIntro.addEventListener('timeupdate', () => {
        if (!hasRevealedUI && videoIntro.currentTime >= transitionTime) {
          revealUI();
        }
      });

      // 2. Video 1 Ended completely -> Switch to Video 2 for continuous looping
      videoIntro.addEventListener('ended', () => {
        switchToLoopVideo();
      });

      // Attempt playback with iOS support
      safePlayVideo(videoIntro);
    }

    // Fallback timer for UI reveal in case video stalls or is blocked
    setTimeout(() => {
      if (!hasRevealedUI) {
        revealUI();
      }
    }, (transitionTime + 2.5) * 1000);

    // Skip on click / tap
    heroSection.addEventListener('click', () => {
      if (!hasRevealedUI) {
        revealUI();
      }
      kickstartPlaybackOnUserTouch();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeroVideoSequence);
  } else {
    initHeroVideoSequence();
  }
})();
